import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { StepProps, YesNoUnknown, DriverAtIncident, DamageClaimFormData } from '../../types';

const Container = styled.div`
  ${tw`flex flex-col h-full`}
`;

const SectionLabel = styled.div`
  ${tw`text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary mb-2`}
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary mb-6`}
`;

const OptionsContainer = styled.div`
  ${tw`flex flex-col gap-3 mb-8`}
`;

const OptionButton = styled.button<{ isSelected: boolean }>`
  ${tw`w-full p-4 text-left border-2 rounded-card transition-all flex items-center justify-between`}
  ${props =>
    props.isSelected
      ? tw`border-black bg-pink-50`
      : tw`border-border-default hover:border-border-default`}
`;

const OptionText = styled.span`
  ${tw`font-medium text-text-primary`}
`;

const CheckIcon = styled.div<{ isSelected: boolean }>`
  ${tw`w-6 h-6 rounded-full flex items-center justify-center`}
  ${props => (props.isSelected ? tw`bg-black` : tw`bg-bg-secondary`)}
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  ${tw`w-full py-4 font-semibold rounded-card mt-auto`}
  ${props =>
    props.disabled
      ? tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`
      : tw`bg-black text-white hover:bg-black-hover`}
`;

interface AssessmentStepProps extends StepProps {
  question: string;
  subtitle?: string;
  fieldName: keyof DamageClaimFormData;
  showUnknown?: boolean;
  isDriverQuestion?: boolean;
  guestName?: string;
}

const AssessmentStep: React.FC<AssessmentStepProps> = ({
  formData,
  updateFormData,
  onNext,
  question,
  subtitle,
  fieldName,
  showUnknown = true,
  isDriverQuestion = false,
  guestName,
}) => {
  const currentValue = formData[fieldName];

  const handleSelect = (value: YesNoUnknown | DriverAtIncident) => {
    updateFormData({ [fieldName]: value } as Partial<DamageClaimFormData>);
  };

  const handleContinue = () => {
    if (currentValue) {
      onNext();
    }
  };

  // Driver question has different options
  if (isDriverQuestion) {
    const driverOptions = [
      {
        value: DriverAtIncident.GUEST,
        label: guestName || 'Guest',
      },
      {
        value: DriverAtIncident.SOMEONE_ELSE,
        label: 'Someone else',
      },
      {
        value: DriverAtIncident.NOT_BEING_DRIVEN,
        label: 'The vehicle was not being driven when the incident occurred',
      },
      {
        value: DriverAtIncident.UNKNOWN,
        label: "I don't know",
      },
    ];

    return (
      <Container>
        <SectionLabel>DAMAGE DETAILS</SectionLabel>
        <Title>{question}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}

        <OptionsContainer>
          {driverOptions.map(option => (
            <OptionButton
              key={option.value}
              isSelected={currentValue === option.value}
              onClick={() => handleSelect(option.value)}
            >
              <OptionText>{option.label}</OptionText>
              <CheckIcon isSelected={currentValue === option.value}>
                {currentValue === option.value && (
                  <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
                )}
              </CheckIcon>
            </OptionButton>
          ))}
        </OptionsContainer>

        <ContinueButton disabled={!currentValue} onClick={handleContinue}>
          Continue
        </ContinueButton>
      </Container>
    );
  }

  // Standard Yes/No/Unknown options
  const standardOptions = [
    { value: YesNoUnknown.YES, label: 'Yes' },
    { value: YesNoUnknown.NO, label: 'No' },
    ...(showUnknown ? [{ value: YesNoUnknown.UNKNOWN, label: "I don't know" }] : []),
  ];

  return (
    <Container>
      <SectionLabel>DAMAGE DETAILS</SectionLabel>
      <Title>{question}</Title>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}

      <OptionsContainer>
        {standardOptions.map(option => (
          <OptionButton
            key={option.value}
            isSelected={currentValue === option.value}
            onClick={() => handleSelect(option.value)}
          >
            <OptionText>{option.label}</OptionText>
            <CheckIcon isSelected={currentValue === option.value}>
              {currentValue === option.value && (
                <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
              )}
            </CheckIcon>
          </OptionButton>
        ))}
      </OptionsContainer>

      <ContinueButton disabled={!currentValue} onClick={handleContinue}>
        Continue
      </ContinueButton>
    </Container>
  );
};

export default AssessmentStep;
